import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import {
  Users, Building2, Grid3X3, BookmarkCheck, Handshake,
  Calendar, UserPlus, Newspaper, Image, MessageSquare, Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAT_CARDS = [
  { key: 'forum_registrations', label: 'Inscriptions', icon: UserPlus, color: 'bg-blue-500', link: '/admin/inscriptions' },
  { key: 'intervenants', label: 'Intervenants', icon: Users, color: 'bg-amber-500', link: '/admin/intervenants' },
  { key: 'exhibitors', label: 'Exposants', icon: Building2, color: 'bg-emerald-500', link: '/admin/exposants' },
  { key: 'stands', label: 'Stands', icon: Grid3X3, color: 'bg-violet-500', link: '/admin/stands' },
  { key: 'pending_reservations', label: 'Réserv. en attente', icon: Clock, color: 'bg-rose-500', link: '/admin/reservations' },
  { key: 'partenaires', label: 'Partenaires', icon: Handshake, color: 'bg-teal-500', link: '/admin/partenaires' },
  { key: 'program_events', label: 'Programme', icon: Calendar, color: 'bg-indigo-500', link: '/admin/programme' },
  { key: 'news_articles', label: 'Actualités', icon: Newspaper, color: 'bg-orange-500', link: '/admin/actualites' },
  { key: 'gallery_images', label: 'Galerie', icon: Image, color: 'bg-pink-500', link: '/admin/galerie' },
  { key: 'survey_responses', label: 'Messages', icon: MessageSquare, color: 'bg-cyan-500', link: '/admin/messages' },
];

export default function DashboardOverview() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(res.data);
      } catch (e) {
        logger.error('Stats error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-[#1E3A5F] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-overview">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#1E3A5F]">Tableau de bord</h1>
        <p className="text-[#6B7C93] mt-1">Vue d'ensemble de la plateforme URBANIK</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {STAT_CARDS.map((card) => (
          <Card
            key={card.key}
            className="cursor-pointer hover:shadow-md transition-shadow border-0 shadow-sm"
            onClick={() => navigate(card.link)}
            data-testid={`stat-${card.key}`}
          >
            <CardContent className="p-5">
              <div className={`w-10 h-10 ${card.color} rounded-lg flex items-center justify-center mb-3`}>
                <card.icon className="w-5 h-5 text-white" />
              </div>
              <p className="text-2xl font-bold text-[#1E3A5F]">{stats?.[card.key] ?? 0}</p>
              <p className="text-xs text-[#6B7C93] mt-0.5">{card.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <h3 className="font-semibold text-[#1E3A5F] mb-3">Actions rapides</h3>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Nouvel intervenant', link: '/admin/intervenants', icon: Users },
                { label: 'Nouvel exposant', link: '/admin/exposants', icon: Building2 },
                { label: 'Nouvel article', link: '/admin/actualites', icon: Newspaper },
                { label: 'Voir réservations', link: '/admin/reservations', icon: BookmarkCheck },
              ].map((action) => (
                <button
                  key={action.label}
                  onClick={() => navigate(action.link)}
                  className="flex items-center gap-2 p-3 rounded-lg text-sm text-[#1E3A5F] hover:bg-[#F0F2F5] transition-colors text-left"
                  data-testid={`quick-action-${action.link.split('/').pop()}`}
                >
                  <action.icon className="w-4 h-4 text-[#6B7C93]" />
                  {action.label}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <h3 className="font-semibold text-[#1E3A5F] mb-3">Résumé</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-[#6B7C93]">Stands réservés</span>
                <span className="font-semibold text-[#1E3A5F]">
                  {(stats?.stand_reservations || 0)} / {(stats?.stands || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-[#6B7C93]">Articles publiés</span>
                <span className="font-semibold text-[#1E3A5F]">
                  {stats?.news_published || 0} / {stats?.news_articles || 0}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-[#6B7C93]">Réservations en attente</span>
                <span className="font-semibold text-rose-500">{stats?.pending_reservations || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
